import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import {
  ChevronDown,
  FileText,
  LogOut,
  Menu,
  Shield,
  UserCircle,
  X,
} from 'lucide-react'
import { Logo } from '@/shared/components/ui/logo'
import { useConsumerSession } from '@/consumer/hooks/use-consumer-session'
import { useConsumerClaims } from '@/consumer/hooks/use-consumer-claims'
import { useSelfRegistrationEnabled } from '@/consumer/hooks/use-self-registration'
import { headlessLogout } from '@/shared/lib/auth-api'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'

type NavItem = { to: string; label: string; show?: boolean }

export function ConsumerShell({ children }: { children: React.ReactNode }) {
  const { data: session } = useConsumerSession()
  const { data: claims } = useConsumerClaims()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [accountOpen, setAccountOpen] = useState(false)
  const accountMenuRef = useRef<HTMLDivElement>(null)

  const hasActiveClaims = (claims ?? []).length > 0
  // WPS-138 #1: the "Register Product" entry point is hidden for tenants that
  // do not offer consumer self-registration (OC at go-live). Generic + opt-in
  // via Tenant.settings, surfaced through the branding payload.
  const selfRegistrationEnabled = useSelfRegistrationEnabled()

  const navItems: NavItem[] = [
    { to: '/', label: 'Home' },
    { to: '/claims', label: 'Claims', show: hasActiveClaims },
    { to: '/register', label: 'Register Product', show: selfRegistrationEnabled },
  ]

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileMenuOpen])

  // Close both menus on any route change.
  useEffect(() => {
    setMobileMenuOpen(false)
    setAccountOpen(false)
  }, [location.pathname])

  // Close the account dropdown on Escape.
  useEffect(() => {
    if (!accountOpen) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setAccountOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [accountOpen])

  async function handleLogout() {
    setMobileMenuOpen(false)
    setAccountOpen(false)
    // Revokes the session server-side (DELETE) and clears the local token.
    await headlessLogout()
    queryClient.clear()
    window.location.href = '/login'
  }

  const initials = session
    ? `${session.first_name?.[0] ?? ''}${session.last_name?.[0] ?? ''}`.toUpperCase() ||
      session.email[0].toUpperCase()
    : '?'

  const displayName = session
    ? session.first_name
      ? `${session.first_name} ${session.last_name}`.trim()
      : session.email
    : ''

  const avatar = (size: string) => (
    <span
      className={cn(
        'flex items-center justify-center rounded-full border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] text-[var(--green)]',
        size,
      )}
      aria-hidden="true"
    >
      {initials}
    </span>
  )

  const accountLinks = [
    { to: '/account', label: 'Account settings', icon: UserCircle },
    { to: '/terms-and-conditions', label: 'Terms & Conditions', icon: FileText },
    { to: '/privacy-policy', label: 'Privacy Policy', icon: Shield },
  ] as const

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      {/* Header */}
      <header className="sticky top-0 z-10 h-14 border-b-[0.5px] border-b-[var(--rule)] bg-[var(--bg-card)]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-full flex items-center justify-between">
          {/* Left: logo + nav */}
          <div className="flex items-center gap-8">
            <Link to="/" className="cursor-pointer" aria-label="Go to home">
              <Logo size="sm" />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden sm:flex items-center gap-6">
              {navItems
                .filter((item) => item.show !== false)
                .map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) =>
                      cn(
                        'text-base tracking-[0.04em] transition-colors',
                        isActive ? 'font-normal text-[var(--ink)]' : 'text-[var(--ink-3)]',
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
            </nav>
          </div>

          {/* Right: account dropdown (desktop) / hamburger (mobile) */}
          <div className="flex items-center gap-3">
            {/* Desktop account dropdown */}
            <div className="relative hidden sm:block" ref={accountMenuRef}>
              <Button
                variant="ghost"
                onClick={() => setAccountOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={accountOpen}
                className="flex h-9 items-center gap-2 rounded-md px-2 hover:bg-[var(--bg-subtle)]"
              >
                {avatar('h-7 w-7 text-xs')}
                <span className="max-w-[160px] truncate text-base text-[var(--ink-2)]">
                  {displayName}
                </span>
                <ChevronDown
                  className={cn(
                    'h-4 w-4 text-[var(--ink-3)] transition-transform',
                    accountOpen && 'rotate-180',
                  )}
                />
              </Button>

              {accountOpen && (
                <>
                  {/* Invisible scrim catches outside clicks */}
                  <div
                    className="fixed inset-0 z-40"
                    aria-hidden="true"
                    onClick={() => setAccountOpen(false)}
                  />
                  <div
                    aria-label="Account menu"
                    className="absolute right-0 top-[calc(100%+8px)] z-50 w-64 overflow-hidden rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] shadow-lg"
                  >
                    {/* Header */}
                    <div className="flex items-center gap-3 border-b-[0.5px] border-b-[var(--rule)] px-4 py-3">
                      {avatar('h-9 w-9 text-sm')}
                      <div className="min-w-0">
                        <p className="truncate text-sm text-[var(--ink)]">{displayName}</p>
                        {session?.email && (
                          <p className="truncate text-[12px] text-[var(--ink-3)]">
                            {session.email}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Links */}
                    <div className="py-1">
                      {accountLinks.map(({ to, label, icon: Icon }) => (
                        <Link
                          key={to}
                          to={to}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-[var(--ink-2)] transition-colors hover:bg-[var(--bg-subtle)]"
                        >
                          <Icon className="h-4 w-4 text-[var(--ink-3)]" />
                          {label}
                        </Link>
                      ))}
                    </div>

                    {/* Sign out */}
                    <div className="border-t-[0.5px] border-t-[var(--rule)] py-1">
                      <Button
                        variant="ghost"
                        onClick={handleLogout}
                        className="flex w-full items-center justify-start gap-2.5 px-4 py-2.5 text-sm text-[var(--status-error)] hover:bg-[var(--status-error-bg-subtle)]"
                      >
                        <LogOut className="h-4 w-4" />
                        Sign out
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Mobile hamburger */}
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="consumer-mobile-menu"
              className="sm:hidden rounded-[4px] text-[var(--ink-2)]"
            >
              {mobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div
          id="consumer-mobile-menu"
          className="sm:hidden fixed inset-0 top-14 z-40 overflow-y-auto bg-[var(--bg-card)]"
        >
          <div className="max-w-5xl mx-auto px-4 py-4">
            {/* User info */}
            {session && (
              <div className="mb-3 flex items-center gap-3 border-b-[0.5px] border-b-[var(--rule)] pb-3">
                {avatar('h-8 w-8 text-sm')}
                <div className="min-w-0">
                  <p className="truncate text-base text-[var(--ink)]">{displayName}</p>
                  <p className="truncate text-sm text-[var(--ink-3)]">{session.email}</p>
                </div>
              </div>
            )}

            {/* Nav links */}
            <div className="mb-3 flex flex-col border-b-[0.5px] border-b-[var(--rule)] pb-3">
              {navItems
                .filter((item) => item.show !== false)
                .map((item) => (
                  <Button
                    key={item.to}
                    variant="ghost"
                    onClick={() => navigate(item.to)}
                    className="justify-start py-2.5 text-base tracking-[0.04em] text-[var(--ink-2)]"
                  >
                    {item.label}
                  </Button>
                ))}
            </div>

            {/* Account links */}
            <div className="mb-3 flex flex-col border-b-[0.5px] border-b-[var(--rule)] pb-3">
              {accountLinks.map(({ to, label, icon: Icon }) => (
                <Button
                  key={to}
                  variant="ghost"
                  onClick={() => navigate(to)}
                  className="justify-start gap-2.5 py-2.5 text-base tracking-[0.04em] text-[var(--ink-2)]"
                >
                  <Icon className="h-4 w-4 text-[var(--ink-3)]" />
                  {label}
                </Button>
              ))}
            </div>

            {/* Sign out */}
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="justify-start gap-2 py-2.5 text-sm uppercase tracking-[0.1em] text-[var(--ink-2)]"
            >
              <LogOut className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
              Sign out
            </Button>
          </div>
        </div>
      )}

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">{children}</main>
    </div>
  )
}
